import React from 'react';
import { cn } from '@/utils';
import type { ISimulationPlanningCardContentSubItem } from '@/types';
import type { ISimulationPlanningCardContentSection } from '@/types';

interface ContentSectionProps {
  // The section payload from a planning card content block.
  // Includes description text and optional nested bullet items.
  section: ISimulationPlanningCardContentSection;
}

// Items can be either plain text entries or structured objects that may carry
// recursive children under `subItems`.
type NestedItem = string | ISimulationPlanningCardContentSubItem;

/**
 * Normalizes a nested item into its display label.
 *
 * - Strings are rendered directly.
 * - Object items render their `item` field.
 */
const getItemLabel = (item: NestedItem): string => {
  return typeof item === 'string' ? item : (item?.item ?? '');
};

/**
 * Returns nested children for object items.
 *
 * String items do not have children, so this returns null for that branch.
 */
const getNestedItems = (item: NestedItem): NestedItem[] | null => {
  if (typeof item === 'string') return null;
  return item?.subItems ?? null;
};

/**
 * Recursively renders nested bullet list items.
 *
 * Root and nested lists share base typography/list styles, while indentation is
 * controlled per depth to match the original visual behavior.
 */
const renderItems = (
  items: NestedItem[],
  options?: {
    // True only for the first/top-level list.
    isRoot?: boolean;

    // Root indent is chosen by section description presence.
    rootIndentClass?: 'ps-3' | 'ps-6';
  },
): React.ReactNode => {
  // Base styles for all lists, plus conditional indent based on nesting level.
  const LIST_BASE_CLASS = [
    'font-bold leading-6',
    'list-disc list-outside',
    'text-base text-neutral-gray-800',
  ].join(' ');

  // Preserve original behavior:
  // - root list uses description-aware indent (ps-3 or ps-6)
  // - nested lists always use ps-6
  const indentClass = options?.isRoot ? (options.rootIndentClass ?? 'ps-3') : 'ps-6';

  // Render
  return (
    <ul className={cn(LIST_BASE_CLASS, indentClass)}>
      {items.map((item, index) => {
        // Resolve display value and potential children for each node.
        const label = getItemLabel(item);
        const children = getNestedItems(item);

        return (
          <li data-key="section.items.item" key={`${label}-${index}`}>
            {label}
            {/* Recurse only when children exist to keep markup minimal. */}
            {children && children.length > 0 && renderItems(children)}
          </li>
        );
      })}
    </ul>
  );
};

/**
 * ContentSection
 *
 * Renders a single content section within a planning card. Sections can contain
 * a description and/or a list of items, which may be nested.
 *
 * This component intentionally returns null for empty lists to avoid rendering
 * structural wrappers without visible content.
 */
export const ContentSection: React.FC<ContentSectionProps> = ({ section }) => {
  const { description, items } = section ?? {};

  // No bullet content means nothing to render in this section block.
  if (!items || items.length === 0) {
    return null;
  }

  // Keep original spacing behavior:
  // - Sections with description use deeper root indent.
  // - Sections without description use default root indent.
  const topLevelIndent = description ? 'ps-6' : 'ps-3';

  // Render
  return (
    <div data-key="section.items">
      {renderItems(items, { isRoot: true, rootIndentClass: topLevelIndent })}
    </div>
  );
};
